import type { QuerySpec } from '@helios/powersync-client-core';

// Pure helpers live in a dependency-light module so they stay unit-testable.
export {
  assembleTranscript,
  parseStringArrayColumn,
  compareAllergyMention,
  TRANSCRIPT_SEGMENT_DELIMITER,
  segmentPositionLabel,
  buildCombinedTranscript,
  activeSegmentCount,
  COMBINED_TRANSCRIPT_DELIMITER,
} from './voice-recordings-logic';
export type { AllergyMentionMatch } from './voice-recordings-logic';

/* ------------------------------------------------------------------ */
/*  Chief Complaint Voice Recording & Clinical Extraction (PRD.271)   */
/*                                                                    */
/*  Client-side QuerySpecs + write statements against the PowerSync    */
/*  mirror of:                                                        */
/*    - visit_voice_recordings     (client-authored metadata)         */
/*    - visit_clinical_extractions (server-authored; read-only here)  */
/*                                                                    */
/*  The audio blob lives in private Storage; only metadata syncs.     */
/*  Extraction rows are created by pextractClinicalFromTranscript —    */
/*  there is no client insert helper for them by design.              */
/* ------------------------------------------------------------------ */

// ─── Row Types ───

export type VoiceUploadStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';
export type VoiceTranscriptionStatus = 'pending' | 'completed' | 'failed';

export interface VisitVoiceRecordingRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  visit_id: string | null;
  encounter_id: string | null;
  appointment_id: string | null;
  conversation_id: string | null; // PRD.274
  section_key: string | null;
  storage_path: string | null;
  mime_type: string | null;
  original_filename: string | null;
  size_bytes: number | null;
  duration_ms: number | null;
  transcript: string | null;
  transcript_language: string | null;
  transcription_status: VoiceTranscriptionStatus | string | null;
  transcription_error: string | null;
  upload_status: VoiceUploadStatus | string | null;
  recorded_at: string | null;
  recorded_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

export type ClinicalExtractionStatus = 'processing' | 'completed' | 'failed';

export interface VisitClinicalExtractionRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  visit_id: string | null;
  encounter_id: string | null;
  appointment_id: string | null;
  conversation_id: string | null; // PRD.274
  section_key: string | null;
  source_recording_id: string | null;
  source_transcript: string | null;
  source_transcript_hash: string | null;
  provider: string | null;
  provider_endpoint: string | null;
  model_version: string | null;
  summary: string | null;
  symptoms_json: string | null; // jsonb stringified on client
  diagnoses_json: string | null;
  allergies_json: string | null;
  status: ClinicalExtractionStatus | string | null;
  error_code: string | null;
  error_message: string | null;
  attempt_count: number | null;
  requested_at: string | null;
  completed_at: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

// ─── Queries ───

/** Active recordings for a visit section, chronological (oldest first). */
export function qVoiceRecordingsByVisitSection(
  visitId: string,
  sectionKey: string,
): QuerySpec<[string, string]> {
  return {
    key: ['visit_voice_recordings', 'by-visit-section', visitId, sectionKey] as const,
    sql: `SELECT * FROM visit_voice_recordings
          WHERE visit_id = ? AND section_key = ? AND deleted_at IS NULL
          ORDER BY recorded_at ASC`,
    params: [visitId, sectionKey],
    watch: ['visit_voice_recordings'],
  };
}

/** Most recent extraction (any status) for a visit section, or null. */
export function qLatestExtractionByVisitSection(
  visitId: string,
  sectionKey: string,
): QuerySpec<[string, string]> {
  return {
    key: ['visit_clinical_extractions', 'latest-by-visit-section', visitId, sectionKey] as const,
    sql: `SELECT * FROM visit_clinical_extractions
          WHERE visit_id = ? AND section_key = ? AND deleted_at IS NULL
          ORDER BY created_at DESC
          LIMIT 1`,
    params: [visitId, sectionKey],
    watch: ['visit_clinical_extractions'],
  };
}

// ─── Client-side write statements (executed via PowerSync db.execute) ──────
//
// Recording metadata is client-authored: the browser records + uploads the
// blob, then INSERTs the row. Writes flow back through SyncWrite to Postgres.
// The `visit_voice_recordings` table must be present in the function's
// ALLOWED_WRITE_TABLES for these to land server-side.

export interface InsertVoiceRecordingInput {
  id: string;
  orgId: string;
  clinicId: string | null;
  patientId: string;
  visitId: string;
  encounterId: string | null;
  appointmentId: string | null;
  conversationId: string | null; // PRD.274 — active conversation
  sectionKey: string;
  storagePath: string | null;
  mimeType: string | null;
  originalFilename: string | null;
  sizeBytes: number | null;
  durationMs: number | null;
  transcript: string | null;
  transcriptLanguage: string | null;
  transcriptionStatus: VoiceTranscriptionStatus;
  uploadStatus: VoiceUploadStatus;
  recordedAt: string;
  recordedBy: string | null;
  now: string;
}

/** Insert a new recording metadata row. */
export function insertVoiceRecordingStmt(input: InsertVoiceRecordingInput): {
  sql: string;
  params: unknown[];
} {
  return {
    sql: `INSERT INTO visit_voice_recordings
            (id, org_id, clinic_id, patient_id, visit_id, encounter_id, appointment_id,
             conversation_id, section_key, storage_path, mime_type, original_filename, size_bytes,
             duration_ms, transcript, transcript_language, transcription_status,
             upload_status, recorded_at, recorded_by, created_at, updated_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    params: [
      input.id,
      input.orgId,
      input.clinicId,
      input.patientId,
      input.visitId,
      input.encounterId,
      input.appointmentId,
      input.conversationId,
      input.sectionKey,
      input.storagePath,
      input.mimeType,
      input.originalFilename,
      input.sizeBytes,
      input.durationMs,
      input.transcript,
      input.transcriptLanguage,
      input.transcriptionStatus,
      input.uploadStatus,
      input.recordedAt,
      input.recordedBy,
      input.now,
      input.now,
    ],
  };
}

/** Update upload lifecycle (storage_path + upload_status) after upload resolves. */
export function updateVoiceRecordingUploadStmt(input: {
  id: string;
  storagePath: string | null;
  uploadStatus: VoiceUploadStatus;
  now: string;
}): { sql: string; params: [string | null, string, string, string] } {
  return {
    sql: `UPDATE visit_voice_recordings
          SET storage_path = ?, upload_status = ?, updated_at = ?
          WHERE id = ?`,
    params: [input.storagePath, input.uploadStatus, input.now, input.id],
  };
}

/** Soft-delete a recording (permission-gated in the UI). */
export function softDeleteVoiceRecordingStmt(input: { id: string; now: string }): {
  sql: string;
  params: [string, string, string];
} {
  return {
    sql: `UPDATE visit_voice_recordings
          SET deleted_at = ?, updated_at = ?
          WHERE id = ?`,
    params: [input.now, input.now, input.id],
  };
}
